import React, { useState } from 'react';
import Icon from 'components/AppIcon';

const NotificationSettings = ({ language, setLanguage }) => {
  const [settings, setSettings] = useState({
    emailNotifications: {
      caseUpdates: true,
      courtDates: true,
      documentUploads: true,
      invoices: true,
      appointments: true,
      generalUpdates: false
    },
    smsNotifications: {
      caseUpdates: false,
      courtDates: true,
      documentUploads: false,
      invoices: true,
      appointments: true,
      generalUpdates: false
    },
    pushNotifications: {
      caseUpdates: true,
      courtDates: true,
      documentUploads: true,
      invoices: false,
      appointments: true,
      generalUpdates: false
    }
  });

  const [profile, setProfile] = useState({
    name: "Sarah Wanjiku Kamau",
    email: "sarah.kamau@email.com",
    phone: "+254 712 345 678",
    preferredLanguage: language,
    timezone: "Africa/Nairobi",
    twoFactorAuth: true
  });

  const translations = {
    en: {
      settings: "Settings",
      notificationPreferences: "Notification Preferences",
      profileSettings: "Profile Settings",
      securitySettings: "Security Settings",
      languageSettings: "Language Settings",
      emailNotifications: "Email Notifications",
      smsNotifications: "SMS Notifications",
      pushNotifications: "Push Notifications",
      caseUpdates: "Case Updates",
      courtDates: "Court Dates",
      documentUploads: "Document Uploads",
      invoices: "Invoices",
      appointments: "Appointments",
      generalUpdates: "General Updates",
      personalInformation: "Personal Information",
      fullName: "Full Name",
      emailAddress: "Email Address",
      phoneNumber: "Phone Number",
      preferredLanguage: "Preferred Language",
      timezone: "Timezone",
      twoFactorAuth: "Two-Factor Authentication",
      enabled: "Enabled",
      disabled: "Disabled",
      enable: "Enable",
      disable: "Disable",
      saveChanges: "Save Changes",
      changePassword: "Change Password",
      currentPassword: "Current Password",
      newPassword: "New Password",
      confirmPassword: "Confirm Password",
      english: "English",
      swahili: "Kiswahili",
      selectLanguage: "Select Language",
      notificationDescription: "Choose how you want to receive notifications about your legal matters",
      securityDescription: "Manage your account security and authentication settings",
      profileDescription: "Update your personal information and preferences"
    },
    sw: {
      settings: "Mipangilio",
      notificationPreferences: "Mapendeleo ya Arifa",
      profileSettings: "Mipangilio ya Wasifu",
      securitySettings: "Mipangilio ya Usalama",
      languageSettings: "Mipangilio ya Lugha",
      emailNotifications: "Arifa za Barua Pepe",
      smsNotifications: "Arifa za SMS",
      pushNotifications: "Arifa za Kusukuma",
      caseUpdates: "Masasisho ya Kesi",
      courtDates: "Tarehe za Mahakama",
      documentUploads: "Kupakia Hati",
      invoices: "Bili",
      appointments: "Miadi",
      generalUpdates: "Masasisho ya Jumla",
      personalInformation: "Taarifa za Kibinafsi",
      fullName: "Jina Kamili",
      emailAddress: "Anwani ya Barua Pepe",
      phoneNumber: "Nambari ya Simu",
      preferredLanguage: "Lugha Inayopendelewa",
      timezone: "Eneo la Wakati",
      twoFactorAuth: "Uthibitishaji wa Hatua Mbili",
      enabled: "Imewashwa",
      disabled: "Imezimwa",
      enable: "Washa",
      disable: "Zima",
      saveChanges: "Hifadhi Mabadiliko",
      changePassword: "Badilisha Nywila",
      currentPassword: "Nywila ya Sasa",
      newPassword: "Nywila Mpya",
      confirmPassword: "Thibitisha Nywila",
      english: "Kiingereza",
      swahili: "Kiswahili",
      selectLanguage: "Chagua Lugha",
      notificationDescription: "Chagua jinsi unavyotaka kupokea arifa kuhusu mambo yako ya kisheria",
      securityDescription: "Dhibiti usalama wa akaunti yako na mipangilio ya uthibitishaji",
      profileDescription: "Sasisha taarifa zako za kibinafsi na mapendeleo"
    }
  };

  const t = translations[language];

  const handleNotificationChange = (type, setting) => {
    setSettings(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [setting]: !prev[type][setting]
      }
    }));
  };

  const handleProfileChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    handleProfileChange('preferredLanguage', newLanguage);
  };

  const handleSaveChanges = () => {
    // Mock save functionality
    console.log('Saving settings:', { settings, profile });
  };

  const NotificationSection = ({ title, type, description }) => (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-heading font-semibold text-text-primary">{title}</h3>
        {description && <p className="text-sm text-text-secondary mt-1">{description}</p>}
      </div>
      <div className="space-y-3">
        {Object.entries(settings[type]).map(([setting, enabled]) => (
          <div key={setting} className="flex items-center justify-between p-3 bg-secondary-50 rounded-lg">
            <div>
              <h4 className="font-medium text-text-primary">{t[setting]}</h4>
              <p className="text-sm text-text-secondary">
                Receive notifications for {t[setting].toLowerCase()}
              </p>
            </div>
            <button
              onClick={() => handleNotificationChange(type, setting)}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors
                ${enabled ? 'bg-primary' : 'bg-secondary-300'}
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                  ${enabled ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-text-primary">{t.settings}</h1>
      </div>

      {/* Profile Settings */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="mb-6">
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">{t.profileSettings}</h2>
          <p className="text-text-secondary">{t.profileDescription}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">{t.fullName}</label>
            <input
              type="text"
              value={profile.name}
              onChange={(e) => handleProfileChange('name', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">{t.emailAddress}</label>
            <input
              type="email"
              value={profile.email}
              onChange={(e) => handleProfileChange('email', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">{t.phoneNumber}</label>
            <input
              type="tel"
              value={profile.phone}
              onChange={(e) => handleProfileChange('phone', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">{t.timezone}</label>
            <select
              value={profile.timezone}
              onChange={(e) => handleProfileChange('timezone', e.target.value)}
              className="w-full px-4 py-3 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value="Africa/Nairobi">Africa/Nairobi (EAT)</option>
              <option value="UTC">UTC</option>
            </select>
          </div>
        </div>
      </div>

      {/* Language Settings */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="mb-6">
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">{t.languageSettings}</h2>
          <p className="text-text-secondary">{t.selectLanguage}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <button
            onClick={() => handleLanguageChange('en')}
            className={`
              flex items-center gap-3 p-4 border-2 rounded-lg transition-smooth
              ${language === 'en' ?'border-primary bg-primary-50 text-primary' :'border-border hover:border-primary-200'
              }
            `}
          >
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-primary">EN</span>
            </div>
            <div className="text-left">
              <h3 className="font-medium">{t.english}</h3>
              <p className="text-sm text-text-secondary">English Language</p>
            </div>
            {language === 'en' && <Icon name="Check" size={20} className="ml-auto" />}
          </button>

          <button
            onClick={() => handleLanguageChange('sw')}
            className={`
              flex items-center gap-3 p-4 border-2 rounded-lg transition-smooth
              ${language === 'sw' ?'border-primary bg-primary-50 text-primary' :'border-border hover:border-primary-200'
              }
            `}
          >
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-primary">SW</span>
            </div>
            <div className="text-left">
              <h3 className="font-medium">{t.swahili}</h3>
              <p className="text-sm text-text-secondary">Lugha ya Kiswahili</p>
            </div>
            {language === 'sw' && <Icon name="Check" size={20} className="ml-auto" />}
          </button>
        </div>
      </div>

      {/* Security Settings */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="mb-6">
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">{t.securitySettings}</h2>
          <p className="text-text-secondary">{t.securityDescription}</p>
        </div>

        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-secondary-50 rounded-lg">
            <div>
              <h3 className="font-medium text-text-primary">{t.twoFactorAuth}</h3>
              <p className="text-sm text-text-secondary">
                Add an extra layer of security to your account
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={`text-sm font-medium ${profile.twoFactorAuth ? 'text-success-700' : 'text-text-secondary'}`}>
                {profile.twoFactorAuth ? t.enabled : t.disabled}
              </span>
              <button
                onClick={() => handleProfileChange('twoFactorAuth', !profile.twoFactorAuth)}
                className={`
                  px-4 py-2 rounded-lg font-medium transition-smooth
                  ${profile.twoFactorAuth 
                    ? 'bg-error-100 text-error-700 hover:bg-error-200' :'bg-primary text-white hover:bg-primary-700'
                  }
                `}
              >
                {profile.twoFactorAuth ? t.disable : t.enable}
              </button>
            </div>
          </div>

          <div className="p-4 bg-secondary-50 rounded-lg">
            <h3 className="font-medium text-text-primary mb-4">{t.changePassword}</h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">{t.currentPassword}</label>
                <input
                  type="password"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">{t.newPassword}</label>
                <input
                  type="password"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-text-primary mb-2">{t.confirmPassword}</label>
                <input
                  type="password"
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Notification Preferences */}
      <div className="bg-surface rounded-lg shadow-card p-6">
        <div className="mb-6">
          <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">{t.notificationPreferences}</h2>
          <p className="text-text-secondary">{t.notificationDescription}</p>
        </div>

        <div className="space-y-8">
          <NotificationSection 
            title={t.emailNotifications} 
            type="emailNotifications"
          />
          <NotificationSection 
            title={t.smsNotifications} 
            type="smsNotifications"
          />
          <NotificationSection 
            title={t.pushNotifications} 
            type="pushNotifications"
          />
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSaveChanges}
          className="flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-700 transition-smooth"
        >
          <Icon name="Save" size={16} />
          <span>{t.saveChanges}</span>
        </button>
      </div>
    </div>
  );
};

export default NotificationSettings;